import { cn } from "@gettactic/helpers";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import React from "react";

export interface FloorplanImportPillProps {
  status: "running" | "ready" | "failed";
  /** Epoch ms when the import started, for the elapsed clock. */
  startedAt?: number;
  onOpen: () => void;
}

const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/** Shown in the toolbar while an import runs with its dialog closed. */
export const FloorplanImportPill: React.FC<FloorplanImportPillProps> = ({
  status,
  startedAt,
  onOpen
}) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (status !== "running") return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status]);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex items-center gap-2 rounded-full border px-3 py-1 font-medium text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        status === "running" && "border-blue-200 bg-blue-50 text-blue-700",
        status === "ready" && "border-green-200 bg-green-50 text-green-700",
        status === "failed" && "border-red-200 bg-red-50 text-red-700"
      )}
    >
      {status === "running" && (
        <Loader2
          size={14}
          className="animate-spin motion-reduce:animate-none"
        />
      )}
      {status === "ready" && <CheckCircle2 size={14} />}
      {status === "failed" && <AlertCircle size={14} />}
      {status === "running" &&
        `Building your map${startedAt ? ` · ${formatElapsed(now - startedAt)}` : ""}`}
      {status === "ready" && "Your map is ready · Review"}
      {status === "failed" && "Import needs attention"}
    </button>
  );
};
